import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdMomentRowVm } from './hd-moments.models';

/** null = gesloten. */
export interface HdMomentsDialogTarget {
  chartName: string;
}

const PAGE_SIZE = 15;

/**
 * "Alle momenten…"-dialog (spec notable moments §4.4): de volledige
 * momenten-lijst van een chart met zoeken (client-side op naam — de relaties
 * zijn al volledig geladen) en paginering.
 */
@Component({
  selector: 'hd-moments-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule],
  templateUrl: './hd-moments-dialog.component.html',
  styleUrl: './hd-moments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentsDialogComponent {
  readonly target = model<HdMomentsDialogTarget | null>(null);
  /** Alle momenten-rijen (chronologisch aflopend, gebouwd door de sectie). */
  readonly rows = input<readonly HdMomentRowVm[]>([]);
  /** Klik op een moment: open die chart (dialog sluit mee). */
  readonly chartOpenRequested = output<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly search = signal('');
  protected readonly page = signal(1);
  protected readonly pageSize = PAGE_SIZE;

  constructor() {
    effect(() => {
      if (this.target()) {
        this.search.set('');
        this.page.set(1);
      }
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.advancedPanel.moments.title'),
      searchPlaceholder: translate('humanDesign.advancedPanel.moments.searchPlaceholder'),
      none: translate('humanDesign.advancedPanel.moments.none'),
      hasNote: translate('humanDesign.advancedPanel.moments.hasNote'),
      hasAttachments: translate('humanDesign.advancedPanel.moments.hasAttachments'),
    };
  });

  protected readonly filteredRows = computed<readonly HdMomentRowVm[]>(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.rows();
    return this.rows().filter(row => row.name.toLowerCase().includes(term));
  });

  protected readonly pageRows = computed<readonly HdMomentRowVm[]>(() => {
    const start = (this.page() - 1) * PAGE_SIZE;
    return this.filteredRows().slice(start, start + PAGE_SIZE);
  });

  protected onSearchChange(value: string): void {
    this.search.set(value);
    this.page.set(1);
  }

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected openRow(row: HdMomentRowVm): void {
    if (!row.relation.otherChartId) return;
    this.chartOpenRequested.emit(row.relation.otherChartId);
    this.target.set(null);
  }
}
